import { useEffect, useRef, useCallback } from 'preact/hooks';
import { signalRService } from '@/services/signalRService';
/**
 * Manages SignalR group subscriptions and event handlers for asset review.
 *
 * Handles:
 * - Joining/leaving the asset group for real-time comments
 * - Tracking upload status handlers and cleaning them up on unmount/asset change
 * - Pre-joining user group for URL uploads (race condition prevention)
 */
export function useAssetSignalR({ assetId, userId, onCommentAdded, onUploadCompleted, onUploadFailed, }) {
    // Track active upload handler so we can clean it up on unmount or asset change
    const uploadHandlerRef = useRef(null);
    const cleanupUploadHandler = useCallback(() => {
        const entry = uploadHandlerRef.current;
        if (entry) {
            signalRService.off('UploadStatusChanged', entry.handler);
            signalRService.leaveGroup(entry.group);
            uploadHandlerRef.current = null;
        }
    }, []);
    // Subscribe to real-time comments for the current asset
    useEffect(() => {
        if (!assetId)
            return;
        const group = `asset-${assetId}`;
        let cancelled = false;
        const handleCommentAdded = onCommentAdded;
        signalRService.joinGroup(group).then(() => {
            if (cancelled) {
                signalRService.leaveGroup(group);
                return;
            }
            signalRService.on('CommentAdded', handleCommentAdded);
        });
        return () => {
            cancelled = true;
            signalRService.off('CommentAdded', handleCommentAdded);
            signalRService.leaveGroup(group);
        };
    }, [assetId, onCommentAdded]);
    // Clean up any lingering upload handler when asset changes or component unmounts
    useEffect(() => {
        return () => cleanupUploadHandler();
    }, [assetId, cleanupUploadHandler]);
    /**
     * Subscribe to upload status for a pending version (e.g. after loading asset data
     * and finding an in-progress URL upload).
     */
    const subscribeToUploadStatus = useCallback((versionId) => {
        if (!userId)
            return;
        // Clean up any previous upload subscription
        cleanupUploadHandler();
        const group = `user-${userId}`;
        signalRService.joinGroup(group).then(() => {
            const handleUploadResult = (payload) => {
                const notification = payload;
                if (notification.versionId !== versionId)
                    return;
                // Self-cleanup on receipt
                cleanupUploadHandler();
                if (notification.status === 'Completed') {
                    onUploadCompleted(notification);
                }
                else {
                    onUploadFailed(notification);
                }
            };
            signalRService.on('UploadStatusChanged', handleUploadResult);
            uploadHandlerRef.current = { handler: handleUploadResult, group };
        });
    }, [userId, cleanupUploadHandler, onUploadCompleted, onUploadFailed]);
    /**
     * Pre-join the user group BEFORE an upload API call to prevent race conditions
     * where small files finish before the client subscribes.
     * Returns a cleanup function if the upload API call fails.
     */
    const preJoinForUpload = useCallback(async (versionId) => {
        if (!userId)
            return () => { };
        // Clean up any previous upload subscription
        cleanupUploadHandler();
        const group = `user-${userId}`;
        await signalRService.joinGroup(group);
        const handleUploadResult = (payload) => {
            const notification = payload;
            if (notification.versionId !== versionId)
                return;
            cleanupUploadHandler();
            if (notification.status === 'Completed') {
                onUploadCompleted(notification);
            }
            else {
                onUploadFailed(notification);
            }
        };
        signalRService.on('UploadStatusChanged', handleUploadResult);
        uploadHandlerRef.current = { handler: handleUploadResult, group };
        // Return cleanup for use if the API call fails
        return () => cleanupUploadHandler();
    }, [userId, cleanupUploadHandler, onUploadCompleted, onUploadFailed]);
    return {
        subscribeToUploadStatus,
        preJoinForUpload,
    };
}
